/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import { Transaction, Auth, User, CloudObject, WorkflowObjectState } from 'olympe';
import { getLogger } from '@olympeio/core';
import { registerDataGridRenderer } from './DataGridRegistry.jsx';
import DataGridConfig, { getValueFromPath } from './DataGridConfig.js';
import { stringAvatar, TARGET_BCP_MAP } from '../../section_sf/Utils.js';
import { useForm } from 'react-hook-form';
import { TextFieldElement } from 'react-hook-form-mui';
import { format } from 'date-fns';
import { EditAssuranceRenderer } from '../../section_bcp/renderers/EditAssuranceRenderer.jsx'
import { AlternateMVLinkedRenderer } from '../../section_bcp/renderers/AlternateMVLinkedRenderer.jsx'
import { VendorDetailsRenderer } from '../../section_bcp/renderers/VendorDetailsRenderer.jsx'
import { useObjectProperty } from "../_helpers.js"
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField'
import Icon from '@mui/material/Icon';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import {
    canAcceptOrReject, canDelete, checkBcpInputEdit, canRejectDisable, getCurrentWorkflow, BCP_WORKFLOW_STATES, canDisablePriorityTargetBCP,
    workflowStatesForApplyButtonDisabled, checkBcpInputSaving
} from '../../../common/section_bcp/BcpWorkflowAuthorizations';
import { BCPAzureUserDropDown } from '../../section_bcp/renderers/BCPAzureUserDropDown.jsx'
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import BCP from '../../../common/section_bcp/DM_BCP.js';
import { styled } from '@mui/material/styles';
import { useGridApiContext } from '@mui/x-data-grid-pro';
import { getApplicationContext } from '../../../common/utils/Context';
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';


/**
 * TEXTS
 * Render values as a string
 * - lineBreak: boolean = true
 * - separator: string = ' ' <- used if lineBreak===false
 * - placeholder: string = 'NO VALUE'
 * - dateFormat: string
 */
const TextEditableComponent = props => {
    const { control, handleSubmit } = useForm({
        mode: 'all',
        defaultValues: { [props.name]: props.value || '' }
    });
    const onChange = handleSubmit((data) => {
        const newValue = data[props.name];
        props.onChange(newValue);
    });
    return (
        <TextFieldElement
            control={control}
            name={props.name}
            type={props.type}
            validation={{
                required: props.required,
                pattern: props.pattern ? {
                    value: new RegExp(props.pattern.value, 'g'),
                    message: props.pattern.message
                } : null
            }}
            onChange={onChange}
            onBlur={onChange}
            autoFocus={true}
            margin={'dense'}
            size={'small'}
            fullWidth={true}
            sx={props.sx}
        />
    );
};
registerDataGridRenderer({
    name: 'TEXTS',
    render: (values, options) => {
        const lineBreak = options?.lineBreak !== undefined ? options.lineBreak : true;
        const separator = options?.separator !== undefined ? options.separator : ' ';
        const placeholder = options?.placeholder !== undefined ? options.placeholder : '';
        const dateFormat = options?.dateFormat;
        return (
            <Typography variant={'body2'}>
                {values.flatMap((value, index) => {
                    const strValue = value?.toString();
                    let finalValue = strValue || placeholder;

                    // Date case
                    if (dateFormat) {
                        finalValue = formatDate(finalValue, dateFormat);
                    }

                    // Done
                    return index < values.length - 1
                        ? [finalValue, lineBreak ? (<br key={index} />) : separator]
                        : [finalValue];
                })}
            </Typography>
        );
    },
    renderForEdition: (value, object, property, $, options, column, deleteFun) => {
        const type = options?.validation?.type !== undefined ? options.validation.type : 'text';
        const required = options?.validation?.required !== undefined ? options.validation.required : false;
        const pattern = options?.validation?.pattern !== undefined ? options.validation.pattern : null;
        const name = `dg_texts_edition_${property.getTag()}`;
        const sx = options?.editModeSx !== undefined ? options.editModeSx : {};
        deleteFun();
        return (
            <TextEditableComponent
                name={name}
                type={type}
                required={required}
                pattern={pattern}
                value={value}
                onChange={newValue => {
                    updateProperty($, object, property, newValue);
                }}
                sx={sx}

            />
        );
    }
});

// For target BCP
registerDataGridRenderer({
    name: 'LABEL_TEXT',
    render: (values) => {
        getLogger('DataGridActionsWrapper').info("values for target BCP", values);
        return (
            <Tooltip title={TARGET_BCP_MAP[values]} placement={'top'} enterDelay={500} leaveDelay={200} followCursor>
                <Box sx={{ width: '360px', height: '30px' }}>
                    <Typography variant={'body2'} >
                        {values.flatMap((value) => {
                            const strValue = value?.toString();
                            let finalValue = TARGET_BCP_MAP[strValue];
                            // Done
                            return finalValue;
                        })}
                    </Typography>
                </Box>
            </Tooltip>
        );
    },
});

/**
 * ICON_ACTIONS
 * Render icon buttons and register the onClick event on them
 * - actions: Array<string> = []
 */
// {   "actions": [     "Accept",     "Reject"   ] }
registerDataGridRenderer({
    name: 'ICON_ACTIONS',
    render: (_values, options, object, $) => {
        const actions = options?.actions || [];
        const color = options?.color || 'inherit';
        const email = CloudObject.get(Auth.getCurrentUser()).get(User.loginProp);
        return (
            <div>
                {actions.map((action, index) => {
                    return (
                        <IconButton
                            key={index}
                            disabled={!(
                                // enable if can delete and action is deletion
                                canDelete($.get('Is Admin'), email, object) && action === 'deleteForever'
                                // enable if action is not deletion
                                // change next lines for other logic to enable button
                                || action !== 'deleteForever'
                            )}
                            onClick={() => {
                                $.set('Action Clicked', action);
                                $.set('Actioned Object', object);
                                $.trigger('On Action Clicked');
                            }}
                            sx={{ margin: 'auto', color: color }}
                        >
                            <Icon>{action}</Icon>
                        </IconButton>
                    );
                })}
            </div>
        );
    }
});

/**
 * MD_CELL
 * Render values as primary and secondary line items
 * - placeholder: string = 'NO VALUE'
 * - noLeadingZeros: boolean = false
 */
registerDataGridRenderer({
    name: 'MD_CELL',
    render: (values, options) => {
        const [_id, description] = values;
        const id = options?.noLeadingZeros ? _id?.replace(/^0+/, '') : _id;
        const placeholder = options?.placeholder ?? 'No Value';
        return (<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'space-around' }}>
            <Typography
                variant="body2"
                color="text.primary"
            >
                {id || placeholder}
            </Typography>
            <Typography
                variant="body2"
                color="text.secondary"
            >
                {description || placeholder}
            </Typography>
        </div>);
    }
});


/**
 * MD_CELL_VENDOR
 * Render values as primary and secondary line items
 * - placeholder: string = 'NO VALUE'
 * - noLeadingZeros: boolean = false
 */
registerDataGridRenderer({
    name: 'MD_CELL_VENDOR',
    render: (values, options, object, $, column) => {
        const [_id, description] = values;
        const currentColumnName = column[DataGridConfig.columnNameProp.getTag()];
        const placeholder = options?.placeholder ?? 'No Value';
        return (<VendorDetailsRenderer
            _id={_id}
            description={description}
            currentColumnName={currentColumnName}
            bcp={object}
            placeholder={placeholder}></VendorDetailsRenderer>)

    }
});

/**
 * USER_PROFILE
 * Render value as an avatar and username
 * - placeholder: string = 'NO VALUE'
 */
registerDataGridRenderer({
    name: 'USER_PROFILE',
    render: ([name], options) => {
        const placeholder = options?.placeholder ?? 'No Value';
        const isEditable = options?.editable;

        // Name is defined
        if (name) {
            return (<Tooltip title={name} >
                <Avatar {...stringAvatar(name)} sx={{ margin: 'auto' }} />
            </Tooltip>);
        }
        return isEditable
            ? (<IconButton
                color={'inherit'}
                onClick={() => {
                    // implement showing of dropdown here + update of property using `updateProperty` function
                }}
                sx={{ margin: 'auto' }}
            ><Icon>{'edit'}</Icon></IconButton>)
            : (<Typography variant={'body2'}>{placeholder}</Typography>);
    }
});
// Custom Tooltip for new requirement of showng big text
const CustomWidthTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
))({
    [`& .${tooltipClasses.tooltip}`]: {
        maxWidth: 400,
        color: 'white',
        fontFamily: 'Roboto',
        fontSize: '1rem',
        boxShadow: '2px 2px black'
    },
});

/**
 * ACTIONS
 * Render buttons and register the onClick event on them
 * - actions: Array<string> = []
 * - disable: object, eg: {"forecastStatus":["ACP"]}
 * - disabled: boolean
 */

const DataGridActionsWrapper = ({ actions, options, object, $, canAcceptOrReject, canRejectDisable }) => {
    const [areActionsDisabled, setAreActionsDisabled] = useState(true);

    const applicationContext = getApplicationContext($);
    const [areRejectDisabled, setAreRejectDisabled] = useState(true);
    const [isLoading, setIsLoading] = useState(true);
    getLogger('DataGridActionsWrapper').info('actions', actions);
    const isAdmin = $.get('Is Admin');
    const email = CloudObject.get(Auth.getCurrentUser()).get(User.loginProp);
    let userName = applicationContext.get("userName")
    let qaInFactoryProp = useObjectProperty($, object, BCP.qaInFactoryProp)
    let technicalApplicationGroupProp = useObjectProperty($, object, BCP.technicalApplicationGroupProp)
    let qualityAssuranceProp = useObjectProperty($, object, BCP.qualityAssuranceProp)

    useEffect(() => {
        getLogger('DataGridActionsWrapper').info('inside useEffect');
        async function callCheckPermission() {
            await checkPermissions();
        }
        callCheckPermission();
    }, [qaInFactoryProp,
        technicalApplicationGroupProp,
        qualityAssuranceProp]);

    const checkPermissions = async () => {
        getLogger('DataGridActionsWrapper').info('Inside checkPermissions');
        const canPerformAction = await canAcceptOrReject(isAdmin, email, object, $);
        setAreActionsDisabled(!canPerformAction);
        //check the enable the Reject button for all the stages of BCP Request
        const canRejectAction = await canRejectDisable(isAdmin, email, object, $);
        setAreRejectDisabled(!canRejectAction);
        setIsLoading(false);
    };

    checkPermissions();

    // check action is accept or reject and disable the button condition wise
    const CheckActionDisable = (action) => {
        if (action === 'Accept') {
            return options?.disabled ? (options?.disabled) : areActionsDisabled

        } else if (action === 'Reject') {
            return options?.disabled ? (options?.disabled) : areRejectDisabled

        } else {
            return false;
        }
    }

    // Tooltip for Disabled Accept button
    const getTooltipText = (action) => {
        if (action !== "Accept") return null;
        const currentWorkflowState = getCurrentWorkflow(object, $)?.get(WorkflowObjectState.stateProp);
        const tooltipContent = {
            [BCP_WORKFLOW_STATES.ALT_VS_SUGG_BY_BP]: "Please add AG/CTAG, QA in the Factory to accept BCP request",
            [BCP_WORKFLOW_STATES.ALT_VS_CHECK_BY_PM]: "Please add Alternative Vendor Site, Main Vendor linked, Cost savings, Input Savings Estimate & SQA to accept BCP request",
        }
        if (currentWorkflowState === BCP_WORKFLOW_STATES.ALT_VS_CHECK_BY_PM || currentWorkflowState === BCP_WORKFLOW_STATES.ALT_VS_SUGG_BY_BP) {
            return tooltipContent[currentWorkflowState]
        }
        return null;
    }




    return (
        <div className="not-allowed">
            {!isLoading &&
                actions.map((action, index) => {
                    const buttonSx = {
                        textDecoration: 'underline',
                        textTransform: 'none',
                        '&:hover': {
                            textDecoration: 'underline',
                        },
                    };
                    // disable the accept or reject button condition wise
                    const disableAcceptReject = CheckActionDisable(action)
                    return (
                        <CustomWidthTooltip key={index} title={disableAcceptReject ? getTooltipText(action) : null} variant={'body1'}>
                            <span>
                                <Button
                                    variant="text"
                                    disabled={disableAcceptReject}
                                    sx={buttonSx}
                                    onClick={() => {
                                        $.set('Action Clicked', action);
                                        $.set('Actioned Object', object);
                                        $.trigger('On Action Clicked');
                                    }}
                                >
                                    {action}
                                </Button>
                            </span>
                        </CustomWidthTooltip>
                    );
                })}
        </div>
    );
};

// Action Register DataGrid Renderer with the wrapper component
registerDataGridRenderer({
    name: 'ACTIONS',
    render: (_values, options, object, $) => {
        const actions = options?.actions || [];
        return (
            <DataGridActionsWrapper
                actions={actions}
                options={options}
                object={object}
                $={$}
                canAcceptOrReject={canAcceptOrReject}
                canRejectDisable={canRejectDisable}
            />
        );
    },
});

/**
* ACTION
* Render buttons and register the onClick event on them
* - actions: Array<string> = []
* - disable: object, eg: {"forecastStatus":["ACP"]}
* - disabled: boolean
*/
registerDataGridRenderer({
    name: 'ACTION',
    render: (_values, options, object, $, column, refreshLine, deleteElementsForColumns, setNewCellValue) => {
        const actions = options?.actions || [];
        let emailOfUser = CloudObject.get(Auth.getCurrentUser()).get(User.loginProp);
        const apiRef = useGridApiContext();
        const currentColumnName = column[DataGridConfig.columnNameProp.getTag()];
        const currentWorkflowState = getCurrentWorkflow(object, $)?.get(
            WorkflowObjectState.stateProp
        );
        return (
            <div className="not-allowed">
                {actions.map((action, index) => {
                    const buttonSx = {
                        textDecoration: 'underline',
                        textTransform: 'none',
                        cursor: 'pointer',
                        '&:hover': {
                            textDecoration: 'underline',
                        }
                    };
                    return (
                        <> {
                            // edit stakeholders link should always be enabled and based on current workflow stage stakeholder dropdowns will be either enabled or disabled
                            <EditAssuranceRenderer
                                disabled={workflowStatesForApplyButtonDisabled.includes(
                                    currentWorkflowState
                                )}
                                apiRef={apiRef}
                                buttonSx={buttonSx}
                                context={$}
                                bcpRequest={object}
                                action={action}
                                currentColumnName={currentColumnName}
                                setNewCellValue={setNewCellValue}
                                deleteElementsForColumns={deleteElementsForColumns}
                                canAcceptOrReject={canAcceptOrReject}
                                refreshLine={refreshLine}
                            />
                        }
                        </>
                    );
                })}
            </div>
        );
    },

});


/**
* ACTION
* Render buttons and register the onClick event on them
* - actions: Array<string> = []
* - disable: object, eg: {"forecastStatus":["ACP"]}
* - disabled: boolean
*/
registerDataGridRenderer({
    name: 'VENDOR_ACTION',
    render: (_values, options, object, $, column, refreshLine, deleteElementsForColumns, setNewCellValue) => {
        const actions = options?.actions || [];

        const apiRef = useGridApiContext();
        const currentColumnName = column[DataGridConfig.columnNameProp.getTag()];
        const currentWorkflowState = getCurrentWorkflow(object, $)?.get(
            WorkflowObjectState.stateProp
        );
        return (
            <div className="not-allowed">
                {actions.map((action) => {
                    // disable the accept or reject button condition wise
                    const buttonSx = {
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        textTransform: 'none',
                        '&:hover': {
                            textDecoration: 'underline',
                        }
                    };
                    return (
                        <> {
                            // edit stakeholders link should always be enabled and based on current workflow stage stakeholder dropdowns will be either enabled or disabled
                            <AlternateMVLinkedRenderer
                                disabled={workflowStatesForApplyButtonDisabled.includes(
                                    currentWorkflowState
                                )}
                                apiRef={apiRef}
                                buttonSx={buttonSx}
                                context={$}
                                values={_values}
                                bcpRequest={object}
                                action={action}
                                currentColumnName={currentColumnName}
                                setNewCellValue={setNewCellValue}
                                deleteElementsForColumns={deleteElementsForColumns}
                                canAcceptOrReject={canAcceptOrReject}
                                refreshLine={refreshLine}
                            />
                        }
                        </>
                    );
                })}
            </div>
        );
    },

});

/**
 * AZURE_USER_DROPDOWN
 * Render buttons and register the onClick event on them
 * - actions: Array<string> = []
 * - disable: object, eg: {"forecastStatus":["ACP"]}
 * - disabled: boolean
 */
registerDataGridRenderer({
    name: 'AZURE_USER_DROPDOWN',
    render: (_values, options, object, $, column) => {
        const currentColumnValue = column[DataGridConfig.columnNameProp.getTag()];
        return (
            <>
                <BCPAzureUserDropDown
                    context={$}
                    bcpRequest={object}
                    currentColumnValue={currentColumnValue}
                    column={column}
                ></BCPAzureUserDropDown>
            </>
        );
    },
});
// This function gets the exact Risk traffic light color based on input risk for different categories
const riskBubbleBgColor = (risk) => {
    let bubbleColor = "";
    switch (risk) {
        case 0:
            bubbleColor = '#D00032';
            break;
        case 1:
            bubbleColor = '#F19100';
            break;
        case 2:
            bubbleColor = '#64A80A';
            break;
        default:
            bubbleColor = '#D00032';
            break;
    }
    return bubbleColor;
};

// Get the exact Tooltip value for a specific combination of column and risk value
const getToolTipText = (columnName, risk) => {
    getLogger('getToolTipText').info('Tooltip Data', columnName, risk);
    let toolTipTextMap = {
        "QIR Documents Readiness": { 0: "No Valid documents", 1: "Partially available", 2: "Documents Ready!" },
        "Trial Status": { 0: "No Trial available", 1: "Partially available", 2: "Approved" },
        "VSI": { 0: "Not Approved", 1: "", 2: "Approved" },
        "VAM": { 0: "Not Approved", 1: "Partially Approved", 2: "Approved" },
    };
    return toolTipTextMap[columnName][risk];
}

/**
 * TRAFFIC_LIGHT
 * Render traffic light view only
 */
registerDataGridRenderer({
    name: 'TRAFFIC_LIGHT',
    render: (_values, options, object, $, column) => {
        getLogger('registerDataGridRenderer').info('BCP Data', _values, object);
        const currentTrafficLightValue = column[DataGridConfig.columnNameProp.getTag()];
        const riskValue = _values?.[0] ?? null;
        return (
            <CustomWidthTooltip title={riskValue !== null && currentTrafficLightValue ?
                getToolTipText(currentTrafficLightValue, riskValue) : 'No Value'} >
                <div
                    className={'rc-risk-table-risk-bubble'}
                    style={{
                        backgroundColor: riskValue !== null ? riskBubbleBgColor(riskValue) : 'transparent',
                    }}
                >
                    {/* Added Em Dash Hex code for Null value */}
                    {riskValue === null ? <>&#x2014;</> : ''}
                </div>
            </CustomWidthTooltip>
        );
    }
});



/***
 * Dropdown - Simple
 */

const DropdownWrapper = ({ value, context, object, column, setNewCellValue, refreshLine }) => {
    const [fieldValue, setFieldValue] = useState(value);
    const propFromColumn = column[DataGridConfig.columnNameProp.getTag()]
    let property = {
        "Cost saving": BCP.costSavingsProp,
        "GHG reduction": BCP.ghgReductionProp,
        "Maguire Eligible?": BCP.maguireEligibilityProp
    };
    useEffect(() => {
        setFieldValue(value)
    }, [value]);
    const isAdmin = context.get('Is Admin');
    const email = CloudObject.get(Auth.getCurrentUser()).get(User.loginProp);
    const apiRef = useGridApiContext();
    const disabledEdition = !checkBcpInputEdit(isAdmin, email, object, context);
    refreshLine();
    const handleChange = (event) => {
        setFieldValue(event.target.value);
        updateProperty(context, object, property[propFromColumn], event.target.value);
        setNewCellValue(event.target.value, apiRef);
        refreshLine();
        if (propFromColumn === "Cost saving" && event.target.value === "no") {
            updateProperty(context, object, BCP.inputSavingEstimatesProp, null);
            setNewCellValue(null, apiRef);
            refreshLine();
        }
    };
    return (
        <FormControl sx={{ m: 1, minWidth: 75 }}>
            <Select
                value={fieldValue}
                onChange={handleChange}
                displayEmpty
                disabled={disabledEdition}
                inputProps={{ 'aria-label': 'Without label' }}
            >
                <MenuItem value={'yes'}>Yes</MenuItem>
                <MenuItem value={'no'}>No</MenuItem>
            </Select>
        </FormControl>
    );
}

registerDataGridRenderer({
    name: 'DROPDOWN',
    render: (value, options, object, $, column, refreshLine, setNewCellValue) => {
        return (
            <DropdownWrapper
                value={value[0] || ""}
                context={$}
                object={object}
                column={column}
                setNewCellValue={setNewCellValue}
                refreshLine={refreshLine}

            />
        );
    }
});

//Number Action to render the input savingEstimate field
registerDataGridRenderer({
    name: 'NUMBER',
    render: renderNumberInputSaving(BCP.inputSavingEstimatesProp)
});

function renderNumberInputSaving(property) {
    return (values, _options, object, $, column, refreshLine, setNewCellValue) => {
        const isAdmin = $.get('Is Admin');
        // actioned object is a bcp request
        const email = CloudObject.get(Auth.getCurrentUser()).get(User.loginProp);
        /** object is a BCP request
         * @type {BCP}
         * */
        const bcpRequest = object;
        const disabledEdition = !checkBcpInputSaving(isAdmin, email, bcpRequest, $);
        const apiRef = useGridApiContext();
        const handleChangeInput = (event) => {
            updateProperty($, object, property, event.target.value);
            setNewCellValue(event.target.value, apiRef);
            refreshLine()
        };
        return (
            <TextField
                id="filled-number"
                type="number"
                value={values}
                slotprops={{
                    inputLabel: {
                        shrink: true,
                    },
                }}
                InputProps={{
                    type: "number",
                    sx: {
                        '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                            display: 'none'
                        },
                        '& input[type=number]': {
                            MozAppearance: 'textfield'
                        },
                        width: 130,
                        height: 40
                    }
                }}
                onChange={handleChangeInput}
                disabled={disabledEdition}
            />
        );
    }
}

//Number Action to render the oscar Id field
registerDataGridRenderer({
    name: 'EDITABLE_TEXT',
    render: renderTextOscarId(BCP.oscarIdProp)
});

function renderTextOscarId(property) {
    return (values, _options, object, $, column, refreshLine, setNewCellValue) => {
        const isAdmin = $.get('Is Admin');
        // actioned object is a bcp request
        const email = CloudObject.get(Auth.getCurrentUser()).get(User.loginProp);
        /** object is a BCP request
         * @type {BCP}
         * */
        const bcpRequest = object;
        const disabledEdition = !checkBcpInputEdit(isAdmin, email, bcpRequest, $);
        const apiRef = useGridApiContext();
        const handleChangeInput = (event) => {
            updateProperty($, object, property, event.target.value);
            setNewCellValue(event.target.value, apiRef);
            refreshLine()
        };
        return (
            <TextField
                id="filled-number"
                value={values}
                slotprops={{
                    inputLabel: {
                        shrink: true,
                    },
                }}
                InputProps={{
                    sx: {
                        width: 130,
                        height: 40
                    }
                }}
                onChange={handleChangeInput}
                disabled={disabledEdition}
            />
        );
    }
}


function formatDate(date, dateFormat) {
    try {
        return format(Date.parse(date), dateFormat);
    } catch (error) {
        getLogger('Enchanced Datagrid - Cell Renderer').error('Incorrect Date Format: ' + error.message);
        return date;
    }
}

/**
 * Helper to use in renderForEdition()
 * @param {!BrickContext} $
                * @param {!CloudObject} object
                * @param {!Property} property
                * @param {!*} value
                */
export function updateProperty($, object, property, value) {
    const logger = getLogger('Enhanced DataGrid Edition');
    const t = Transaction.from($);
    t.update(object, property, value);
    Transaction.process($, t)
        .then(() => {
            $.set('Edited Object', object);
            $.trigger('On Object Edited');
            logger.info(`value updated: ${value}`);
        })
        .catch((error) => logger.error(`value not updated: ${property} ${value} -> ${error}`));
}

//edit priority 

const DropdownPriority = ({ value, context, object, column, setNewCellValue, refreshLine }) => {
    const [priorityVal, setPriority] = useState(value);
    const propFromColumn = column[DataGridConfig.columnNameProp.getTag()]
    let property = {
        "Priority": BCP.priorityProp,
    };
    useEffect(() => {
        setPriority(value)
    }, [value]);
    const isAdmin = context.get('Is Admin');
    const email = CloudObject.get(Auth.getCurrentUser()).get(User.loginProp);
    const apiRef = useGridApiContext();
    const disabledEdition = !canDisablePriorityTargetBCP(isAdmin, email, object, context);
    const handleChange = (event) => {
        setPriority(event.target.value);
        updateProperty(context, object, property[propFromColumn], event.target.value);
        setNewCellValue(event.target.value, apiRef);
        refreshLine();
    };
    return (
        <FormControl sx={{ m: 1, minWidth: 75 }}>
            <Select
                value={priorityVal}
                onChange={handleChange}
                displayEmpty
                disabled={disabledEdition}
                inputProps={{ 'aria-label': 'Without label' }}
            >
                <MenuItem value={'high'}>High</MenuItem>
                <MenuItem value={'low'}>Low</MenuItem>
            </Select>
        </FormControl>
    );
}
registerDataGridRenderer({
    name: 'DROPDOWNPRIORITY',
    render: (value, options, object, $, column, refreshLine, setNewCellValue) => {
        return (
            <DropdownPriority
                value={value[0]}
                context={$}
                object={object}
                column={column}
                setNewCellValue={setNewCellValue}
                refreshLine={refreshLine}
            />
        );
    }
});


//edit target BCP
const DropdownTargetBCP = ({ value, context, object, column, setNewCellValue, refreshLine }) => {
    const [targetval, setTarget] = useState(value);
    const propFromColumn = column[DataGridConfig.columnNameProp.getTag()]

    let property = {
        "Target BCP": BCP.targetBCPProp,
    };
    useEffect(() => {
        setTarget(value)
    }, [value]);
    const isAdmin = context.get('Is Admin');
    const email = CloudObject.get(Auth.getCurrentUser()).get(User.loginProp);
    const apiRef = useGridApiContext();
    const disabledEdition = !canDisablePriorityTargetBCP(isAdmin, email, object, context);
    const handleChange = (event) => {
        setTarget(event.target.value);
        updateProperty(context, object, property[propFromColumn], event.target.value);
        setNewCellValue(event.target.value, apiRef);
        refreshLine();
    };
    return (
        <FormControl sx={{ m: 1, minWidth: 75 }}>
            <Select
                value={targetval}
                onChange={handleChange}
                title={TARGET_BCP_MAP[targetval]}
                displayEmpty
                disabled={disabledEdition}
                inputProps={{ 'aria-label': 'Without label' }}
            >
                <MenuItem value={"alternateSiteNewVendor"}>Alternate site with new Vendor</MenuItem>
                <MenuItem value={"alternateSiteSameVendor"}>Alternate site with same Vendor</MenuItem>
                <MenuItem value={"alternativeSiteApprovedPlant"}>Alternative site with new Vendor already approved in other plants</MenuItem>

            </Select>
        </FormControl>
    );
}
registerDataGridRenderer({
    name: 'DropdownTargetBCP',
    render: (value, options, object, $, column, refreshLine, setNewCellValue) => {
        return (
            <DropdownTargetBCP
                value={value[0]}
                context={$}
                object={object}
                column={column}
                setNewCellValue={setNewCellValue}
                refreshLine={refreshLine}
            />
        );
    }
});